/**
 * askLedger — the conversation's memory of which facts have already been asked.
 *
 * THE BUG THIS FIXES (T8.3, live testing): the "Fill in the remaining details"
 * form came back after every single message, usually with questions the user
 * had already answered. Both ask-cards were computed from the LAST assistant
 * message alone, and "I answered this" / "I skipped this" was recorded only on
 * THAT message. So the next turn — whose regenerated draft still carries the
 * same [PLACEHOLDER] tokens, because the fill was spliced into the document
 * HTML and never written back to the form state — asked all over again.
 *
 * The ledger is the missing thread-level state. Every ask-card records what was
 * answered and what was skipped onto its own message; walking the transcript
 * replays those records into one view of the conversation:
 *
 *   answered — fact → the value the user gave. Never asked again; and when the
 *              fact is still a placeholder in a later draft, the known value is
 *              carried forward into it instead of re-opening the question.
 *   skipped  — facts the user explicitly declined ("Skip", "Dismiss"). An
 *              explicit no is an answer: re-asking it is the same defect.
 *
 * Identity is normalized because the same fact is written three ways across the
 * system: the placeholder token "[MEETING DATE]", the request_facts key
 * "meeting_date", and the human label "Meeting date". Mirrors normalizeFactKey
 * in backend/new-ui-generation/chat/chatService.js — keep the two in step.
 */

/** "[MEETING DATE]" / "meeting_date" / "Meeting Date" → "meeting_date". */
export const normalizeFactKey = (raw) => String(raw || '')
  .replace(/^\[|\]$/g, '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

/**
 * Replay every handled ask-card in the transcript.
 *
 * @param {Array} messages  the chat transcript
 * @returns {{answered: Map<string,string>, skipped: Set<string>, tokens: Map<string,string>}}
 *   `tokens` maps a normalized fact back to the literal placeholder token it was
 *   filled under ("[MEETING DATE]"), which is what a document splice needs.
 */
export function buildAskLedger(messages = []) {
  const answered = new Map();
  const skipped = new Set();
  const tokens = new Map();

  const remember = (rawKey, value, token) => {
    const key = normalizeFactKey(rawKey);
    if (!key) return;
    const v = String(value == null ? '' : value).trim();
    if (!v) return;
    answered.set(key, v);
    skipped.delete(key); // a later answer overrides an earlier skip
    if (token) tokens.set(key, token);
  };

  for (const m of messages) {
    if (!m || m.role !== 'assistant') continue;

    for (const card of m.factsRequests || []) {
      if (!card || !card.completed) continue;
      const values = card.answers || {};
      for (const field of card.fields || []) {
        const raw = field && (field.key || field.label);
        const key = normalizeFactKey(raw);
        if (values[field.key] != null && String(values[field.key]).trim()) remember(raw, values[field.key]);
        else if (key && !answered.has(key)) skipped.add(key);
      }
    }

    for (const [token, value] of Object.entries(m.filledPlaceholders || {})) {
      remember(token, value, token);
    }
    for (const token of m.dismissedPlaceholders || []) {
      const key = normalizeFactKey(token);
      if (key && !answered.has(key)) skipped.add(key);
    }
  }

  skipped.delete('');
  return { answered, skipped, tokens };
}

/**
 * Drop fields this conversation has already settled. Returns a NEW array; a
 * field the user answered earlier is removed rather than pre-filled, because
 * the value is applied to the document directly (see carryForwardValues).
 */
export function pruneSettledFields(fields = [], ledger) {
  if (!ledger) return fields;
  return fields.filter((f) => {
    const key = normalizeFactKey(f && (f.key || f.label));
    return !key || !(ledger.answered.has(key) || ledger.skipped.has(key));
  });
}

/**
 * Placeholder tokens in `draft` whose value this conversation already knows.
 * These are what a regenerated draft must be re-filled with rather than
 * re-asked — leak #1 in utils/factCarryForward.js, seen from the chat side.
 *
 * @returns {Object<string,string>} placeholder token → known value
 */
export function carryForwardValues(placeholders = [], ledger) {
  if (!ledger) return {};
  const out = {};
  for (const ph of placeholders) {
    const key = normalizeFactKey(ph);
    const value = key && ledger.answered.get(key);
    if (value) out[ph] = value;
  }
  return out;
}

/* ── Supersede: a typed message outranks the card it answers past ─────────
 *
 * The composer is never hidden or locked while an ask-card is docked — a card
 * and the input are two independent ways into the same conversation, and a user
 * who wants to change course ("actually, forget the bank details, draft the
 * DIR-12 instead") has to be able to say so. Which creates the ambiguity: the
 * user can answer the card AND send a message that contradicts it. Both cannot
 * be applied — the card's answers would land on top of a NEWER instruction,
 * filling in precisely the facts that were just abandoned.
 *
 * THE RULE: the message wins. Sending a free-text message settles the pending
 * card as skipped, writing the SAME record an explicit Skip writes onto the
 * message — so buildAskLedger reads it as settled, pruneSettledFields drops its
 * fields, and those questions can never respawn. An explicit "no" and "I've
 * moved on" are the same answer as far as the thread is concerned.
 *
 * Deliberately NOT covered: the company-confirm card. Declining a question is a
 * legitimate answer ("leave it blank"); recording a company as CONFIRMED that
 * the user never actually looked at would be a false claim about verified data,
 * and isCompanyConfirmSettled would then suppress every later card for it. That
 * card stays docked beside the live composer, and the next turn re-offers it.
 */

/**
 * The record a superseding message leaves on the card it overtook. Identical in
 * shape to a Skip (completed, no answers) — that is the point: neither the
 * ledger above nor the transcript's receipt chrome needs a special case.
 */
export const supersededByChatPatch = () => ({
  completed: true,
  // "skip" is what keeps this on the muted/skipped receipt branch in
  // chat/cards.jsx — a superseded card is a skipped card, said differently.
  completedNote: 'skipped — the user answered in chat',
  // The sharper detail line, for when the receipt starts honouring a per-card
  // one. Harmless until then: cards.jsx's generic "Vee proceeded with
  // placeholders for the rest" is true of a superseded card too, because its
  // facts were declined and the document keeps their placeholders.
  completedDetail: 'You answered in chat instead',
});

/* ── Company confirmation: once per company, per conversation ─────────────
 *
 * The details card ("STEP 1 OF 2 · CONFIRM THE COMPANY") is a different card
 * TYPE from the facts form, so the ledger above never covered it — and the
 * model re-called confirm_company_details on the next turn, putting a second
 * confirm card for the SAME company in front of a user who had just confirmed
 * it. Confirmation is a property of the CONVERSATION, not of one card: the
 * moment any card for company X is confirmed, every later card for X is
 * already settled. (The server suppresses the repeat too — see
 * backend/new-ui-generation/chat/threadFacts.js. This is the client half, and
 * it also covers sessions restored from history.)
 */

/** Company identity, insensitive to case and punctuation. Mirrors threadFacts.js. */
export const normalizeCompanyKey = (raw) => String(raw || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '');

/** Every company identity this thread has already had confirmed. */
export function confirmedCompanies(messages = []) {
  const out = new Set();
  for (const m of messages) {
    if (!m || m.role !== 'assistant') continue;
    for (const card of m.detailsConfirms || []) {
      if (!card || !card.confirmed) continue;
      for (const id of [card.companyCin, card.companyName]) {
        const key = normalizeCompanyKey(id);
        if (key) out.add(key);
      }
    }
  }
  return out;
}

/**
 * Is this confirm-card already settled by an earlier confirmation? A card
 * carrying the user's own proposed corrections is never settled — it is
 * showing them their edit, not re-asking.
 */
export function isCompanyConfirmSettled(card, confirmed) {
  if (!card || !confirmed || !confirmed.size) return false;
  if (Array.isArray(card.proposedFields) && card.proposedFields.length) return false;
  return [card.companyCin, card.companyName]
    .map(normalizeCompanyKey)
    .some((k) => k && confirmed.has(k));
}

/* ── Surface scoping: an ask belongs to the studio that spawned it ────────
 *
 * The chat thread follows the user between studios, and the pending-ask panel
 * was computed from the last assistant message alone — so a resolution drafted
 * on the Agent page arrived in the REPORT studio's dock as a pinned "Fill in
 * the remaining details" form, and because the dock hides its composer while a
 * card is pending, the Report dock could not be typed into at all. The card is
 * still in the transcript (history is never filtered); it just stops being
 * PINNED on a surface that does not own the document it belongs to.
 */

/** Which document a message's ask concerns: its own draft, else the newest before it. */
export function askDraftKind(messages = [], msgIndex = -1) {
  const end = msgIndex >= 0 ? msgIndex : messages.length - 1;
  for (let i = end; i >= 0; i -= 1) {
    const m = messages[i];
    if (!m || m.role !== 'assistant') continue;
    const drafts = Array.isArray(m.drafts) ? m.drafts : [];
    const d = drafts[drafts.length - 1];
    if (d && d.kind) return String(d.kind);
  }
  return null;
}

/**
 * May this ask be PINNED above the composer on this surface?
 *
 * @param {string|null} draftKind  the document the ask concerns (askDraftKind)
 * @param {string|null} surface    'minutes' | 'report' | null (the Agent page,
 *                                 which owns no document and hosts everything)
 */
export function askBelongsToSurface(draftKind, surface) {
  if (!surface) return true; // Agent page: every ask is at home here
  if (!draftKind) return true; // a conversational ask with no document yet
  return draftKind === surface;
}

export default buildAskLedger;

/**
 * Everything this conversation has been TOLD, as a plain label → value map for
 * the wire.
 *
 * The gap this closes: the fill-in form applies its answers straight to the
 * document, client-side. Nothing about them is ever said out loud, so the model
 * never learns them — it sees the resulting document text and no more. Ask it
 * to redraft and the pipeline rebuilds from the company record, and every value
 * the user typed is gone. From the user's side: "filled details are not going
 * to ai".
 *
 * Sending the ledger fixes both halves at once — the model can use the facts,
 * and it stops re-asking for them.
 *
 * Tokens are humanised ("[NEW ADDRESS]" → "New address") because the model
 * reads these as facts, not as placeholder syntax.
 */
const HUMANISE = (key) => String(key || '')
  .replace(/^\[|\]$/g, '')
  .replace(/_/g, ' ')
  .trim()
  .replace(/^./, (c) => c.toUpperCase());

const MAX_KNOWN_FACTS = 40;
const MAX_FACT_CHARS = 300;

export function knownFactsFrom(messages = []) {
  const ledger = buildAskLedger(messages);
  const out = {};
  let n = 0;
  for (const [key, value] of ledger.answered.entries()) {
    if (n >= MAX_KNOWN_FACTS) break;
    // Prefer the original token's wording where we kept one — "[DAY, DATE]"
    // reads better as "Day, date" than the normalised "day_date".
    const label = HUMANISE(ledger.tokens.get(key) || key);
    const v = String(value || '').trim().slice(0, MAX_FACT_CHARS);
    if (!label || !v) continue;
    out[label] = v;
    n += 1;
  }
  return out;
}
